import { Account } from 'types'
import { getAuthCookie } from 'src/utils/cookies'

export interface CanParams {
  resource?: string
  action: string
  params?: { id?: string | number; field?: string; [k: string]: unknown }
}

export interface CanReturnType {
  can: boolean
  reason?: string
}

export const CASBIN_POLICY = `
p, ARCHITECT, dashboard, (list)
p, ARCHITECT, urban_analysis, (list)|(create)
p, ARCHITECT, urban_analysis/*, (edit)|(show)
p, ARCHITECT, projects, (list)|(create)
p, ARCHITECT, projects/*, (edit)|(show)|(create)
p, ARCHITECT, collaborators, (list)|(create)
p, ARCHITECT, collaborators/*, (edit)|(show)
p, ARCHITECT, salesmens, (list)|(create)
p, ARCHITECT, salesmens/*, (edit)|(show)
p, ARCHITECT, companies, (list)|(create)
p, ARCHITECT, companies/*, (edit)|(show)
p, ARCHITECT, invoices, (list)
p, ARCHITECT, clients, (list)|(create)
p, ARCHITECT, clients/*, (edit)|(show)
p, ARCHITECT, tools, (list)
p, ARCHITECT, tools/*, (list)

p, COLLABORATOR, dashboard, (list)
p, COLLABORATOR, urban_analysis, (list)
p, COLLABORATOR, urban_analysis/*, (edit)|(show)
p, COLLABORATOR, projects, (list)|(create)
p, COLLABORATOR, projects/*, (edit)|(show)|(create)
p, COLLABORATOR, clients, (list)|(create)
p, COLLABORATOR, clients/*, (edit)|(show)
p, COLLABORATOR, resources, (list)

p, SALESMAN, projects, (list)|(create)
p, SALESMAN, projects/*, (edit)|(show)|(create)
p, SALESMAN, clients, (list)|(create)
p, SALESMAN, clients/*, (edit)|(show)
p, SALESMAN, urban_analysis, (list)|(create)
p, SALESMAN, urban_analysis/*, (edit)|(show)
p, SALESMAN, resources, (list)

p, SOCIETY, projects, (list)|(create)
p, SOCIETY, projects/*, (edit)|(show)|(create)
p, SOCIETY, urban_analysis, (list)|(create)
p, SOCIETY, urban_analysis/*, (edit)|(show)
p, SOCIETY, clients, (list)|(create)
p, SOCIETY, clients/*, (edit)|(show)
p, SOCIETY, salesmens, (list)|(create)
p, SOCIETY, salesmens/*, (edit)|(show)
p, SOCIETY, resources, (list)

p, CLIENT, dashboard, (list)
p, CLIENT, projects, (list)
p, CLIENT, projects/*, (edit)|(show)
p, CLIENT, resources, (list)
`

type RolePermission = Record<string, string[]>
type PolicyMap = Record<string, RolePermission>

function parsePolicy(text: string): PolicyMap {
  const map: PolicyMap = {}
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim()
    if (!line.startsWith('p,')) continue
    const parts = line.split(',').map((s) => s.trim())
    if (parts.length < 4) continue
    const [, role, obj, actionExpr] = parts
    const actions = actionExpr.match(/[A-Za-z_][A-Za-z0-9_]*/g) ?? []
    const perms = (map[role] ??= {})
    for (const action of actions) {
      ;(perms[action] ??= []).push(obj)
    }
  }
  return map
}

// Port of casbin's keyMatch: '*' is a free segment wildcard. 'projects/*'
// matches 'projects/anything'. The policy ships glob-style resources, and the
// evaluation below is our own (see `canAccess`), so we carry the matcher.
function keyMatch(key: string, pattern: string): boolean {
  if (pattern === key) return true
  if (!pattern.includes('*')) return false
  const re = new RegExp(
    '^' + pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$',
  )
  return re.test(key)
}

const POLICY: PolicyMap = parsePolicy(CASBIN_POLICY)

/**
 * Whether `role` may perform `action` on `obj`.
 *
 * Evaluated purely against the parsed policy: `keyMatch` handles both an
 * exact object (`projects`) and the glob patterns (`projects/*`) the policy
 * ships. We deliberately do NOT delegate to casbin.js's `Authorizer` (the
 * dependency is gone): its manual-mode `setPermission` accumulates grants
 * across calls rather than replacing them, so a single long-lived authorizer
 * leaks one role's permissions into the next — harmless with one role per
 * session, but wrong whenever `canAccess` is asked about several roles in one
 * process.
 */
export async function canAccess(
  role: string,
  action: string,
  obj: string,
): Promise<boolean> {
  const patterns = POLICY[role]?.[action] ?? []
  return patterns.some((p) => keyMatch(obj, p))
}

export const accessControlProvider = {
  can: async ({
    action,
    params,
    resource,
  }: CanParams): Promise<CanReturnType> => {
    const auth = getAuthCookie()
    if (!auth) return { can: false }
    const account: Account = JSON.parse(auth)
    if (action === 'delete' || action === 'edit' || action === 'show') {
      return {
        can: await canAccess(
          account.account_type,
          action,
          `${resource}/${params?.id}`,
        ),
      }
    }
    if (action === 'field') {
      return {
        can: await canAccess(
          account.account_type,
          action,
          `${resource}/${params?.field}`,
        ),
      }
    }
    return {
      can: await canAccess(account.account_type, action, resource ?? ''),
    }
  },
}
